import { useHostedSimulation, useRoom } from '@peterddod/phop';
import { useState } from 'react';
import { BUILD_ID } from '@/config';
import type { HexTileMap } from '@/game/hexmap';
import { isCommand, isSimState } from '@/game/net/protocol';
import { matchContext, simRunning, stepSim } from '@/game/net/simStep';
import { baseRuleSet } from '@/game/rules';
import type { Command, MatchConfig, PlayerId, SimContext, SimState } from '@/game/sim';
import { createInitialState, setupMatch } from '@/game/sim';
import { PERF_ENABLED, perf } from './perf';

interface UseNetworkedSimRunnerResult {
  getState: () => SimState;
  dispatch: (command: Command) => void;
  playerId: PlayerId;
  /** Increments on every state change; use as an effect trigger. */
  tickCount: number;
  isHost: boolean;
  /** The match's island, which the sim steps on (`matchContext`). */
  terrain: HexTileMap;
}

interface UseNetworkedSimRunnerParams {
  /** The host's match (length, map radius, seed), the same on every peer. */
  match: MatchConfig;
  /** Frozen, sorted match roster including self. Treated as stable for the mount. */
  players: string[];
}

const DT = 0.1;
// Heartbeat period for the sim loop; half of DT so each tick lands promptly.
const LOOP_INTERVAL_MS = 50;
// Equal to the catch-up cap: a main thread stalled by rendering (several
// busy tabs) must not slow the match clock, so the end screen shows at 0:00.
const MAX_FRAME_DELTA = 1;
// Most sim time the host will catch up on at once (a stall shouldn't turn
// into a long burst of ticks afterwards).
const MAX_ACCUMULATOR = 1;
const MIGRATION_GRACE_MS = 1000;
const DISCONNECT_RESIGN_MS = 10000;
// Sim seconds between keyframes (full snapshots); the snapshots in between
// are deltas against the previous one. A late-game 4-player keyframe is
// ~100 KiB of JSON (~14 KiB deflated) and a delta ~20 KiB (~3 KiB).
// phop's default `getId` matches array elements with an `id` (`shots`) by
// it; cells and numbers (paths, footprints) match by position.
const KEYFRAME_INTERVAL = 1;

/**
 * The host's sim worker (sim.worker.ts, bundled by scripts/build-worker.ts
 * to the site root), or none where Web Workers aren't available or with
 * `?simworker=0` (to compare against main-thread stepping); phop then steps
 * on the main thread, as it also does if the worker fails. Its file name
 * isn't hashed, so the build id in the query ties it to this build: every
 * player in a lobby must run the same build, and a stale cached worker
 * would host with old sim code past the lobby's build check.
 */
function simWorker(): (() => Worker) | undefined {
  if (typeof Worker === 'undefined') return undefined;
  if (new URLSearchParams(window.location.search).get('simworker') === '0') return undefined;
  const url = new URL('/sim.worker.js', window.location.href);
  url.searchParams.set('v', BUILD_ID);
  return () => new Worker(url, { type: 'module' });
}

/**
 * Host-authoritative networked sim runner, on phop's `useHostedSimulation`.
 *
 * All peers create a deterministic-identical initial state from the same
 * (match, players), on the island and base rules `matchContext` gives the
 * worker too. The elected host (lowest-sorted present player)
 * runs the sim loop and broadcasts snapshots; non-hosts forward commands to
 * it and adopt its snapshots: a keyframe every KEYFRAME_INTERVAL and on a
 * host change or a peer joining, deltas in between, each checked with
 * `isSimState` once applied. phop handles host migration and handover, the
 * cut-off hold, and the hidden-tab-safe ticker. While this peer is host, the
 * sim steps in a Web Worker (`simWorker`), which also encodes the snapshots;
 * the main thread renders from the worker's frames the way a client renders
 * from snapshots, and steps with `step` itself only without a worker. Players missing for
 * DISCONNECT_RESIGN_MS are resigned through a normal `resign` command, so
 * every state change still goes through `tick()`.
 */
export function useNetworkedSimRunner({
  match,
  players,
}: UseNetworkedSimRunnerParams): UseNetworkedSimRunnerResult {
  const { peerId } = useRoom();
  // Fixed for the mount, like the roster.
  const [ctx] = useState<SimContext>(() => matchContext(baseRuleSet, match));
  const [playerId] = useState<PlayerId>(peerId);
  const [worker] = useState(simWorker);

  const sim = useHostedSimulation<SimState, Command>('match', {
    players,
    init: () =>
      createInitialState(ctx, setupMatch(ctx.terrain, match.seed, players, ctx.rules), match),
    step: (state, inputs, dt) => {
      const start = PERF_ENABLED ? performance.now() : 0;
      stepSim(state, ctx, inputs, dt);
      if (PERF_ENABLED) perf.simTick.add(performance.now() - start);
    },
    validateInput: isCommand,
    validateState: isSimState,
    keyframeInterval: KEYFRAME_INTERVAL,
    dt: DT,
    loopIntervalMs: LOOP_INTERVAL_MS,
    maxFrameDelta: MAX_FRAME_DELTA,
    maxCatchUp: MAX_ACCUMULATOR,
    migrationGraceMs: MIGRATION_GRACE_MS,
    absence: { timeoutMs: DISCONNECT_RESIGN_MS, toInput: () => ({ type: 'resign' }) },
    isActive: (state, id) => !!state.players[id] && !state.players[id].eliminated,
    isRunning: simRunning,
    worker,
  });

  return {
    getState: sim.getState,
    dispatch: sim.dispatch,
    playerId,
    tickCount: sim.version,
    isHost: sim.isHost,
    terrain: ctx.terrain,
  };
}
